import React, { PureComponent } from "react";
import { Link } from "react-router-dom";
import * as Icon from "react-feather";
import moment from "moment";

import { fmt } from "../backoffice/backofficeUtils";
import { backofficeCell, stripSummary, SERVICE_COLORS, SERVICE_SHORT, STATUS_STYLES, serviceLabel } from "./backofficeTracker";

/**
 * Backoffice work inside the engagement tracker, read-only.
 *
 * - <BackofficeStrip>: one compact row per client ("Rolled up" mode), expandable.
 * - <BackofficeBand>: the band under the strip: current engagement per service + last completed.
 * - <BackofficeDivider> + <BackofficeRow>: one muted grid row per current engagement ("Rows" mode),
 *   laid out on the tracker's own columns, expandable to its tasks.
 *
 * Nothing here edits anything; the data comes from backofficeService.trackerSummary.
 */

function hours(n) {
	const v = Math.round((n || 0) * 100) / 100;
	return Number.isInteger(v) ? String(v) : String(v).replace(/0$/, "");
}

function StatusPill({ status, small }) {
	const s = STATUS_STYLES[status] || STATUS_STYLES.not_started;
	return (
		<span className={"et-bo-status" + (small ? " et-bo-status--sm" : "")} style={{ background: s.bg, color: s.fg }}>
			<i />
			{s.label}
		</span>
	);
}

function ServiceDots({ services }) {
	return (
		<span className="et-bo-dots" title={(services || []).map(serviceLabel).join(", ")}>
			{(services || []).map(s => (
				<i key={s} style={{ background: SERVICE_COLORS[s] || "#9aa0b0" }} />
			))}
		</span>
	);
}

function clientHref(group) {
	return group.client_id && group.client_type ? `/clients/${group.client_type}/${group.client_id}` : null;
}

export class BackofficeStrip extends PureComponent {
	onToggle = e => {
		e.preventDefault();
		e.stopPropagation();
		this.props.onToggle(this.props.group.key);
	};

	render() {
		const { group, expanded } = this.props;
		const s = stripSummary(group.backoffice);
		const href = clientHref(group);
		return (
			<div className={"et-grid__row et-grid__row--grouped et-bo-strip ft-et-bo-strip" + (expanded ? " et-bo-strip--open" : "")} data-bo-client={group.client_id}>
				<div className="et-grid__td et-grid__td--anchor et-grid__td--anchor-exp">
					<button className={"et-row-exp ft-et-bo-strip-toggle " + (expanded ? "et-row-exp--open" : "")} onClick={this.onToggle} title={expanded ? "Hide backoffice engagements" : "Show backoffice engagements"}>
						<Icon.ChevronRight size={13} />
					</button>
					<span className="et-bo-tag">
						<Icon.Briefcase size={10} />
						Backoffice
					</span>
					<ServiceDots services={s.services} />
					<span className="et-bo-strip__services">{s.services.map(x => SERVICE_SHORT[x] || x).join(" · ")}</span>
				</div>
				<div className="et-grid__td et-bo-strip__summary">
					<span className="et-bo-muted">{s.counts}</span>
					{s.periods && <span className="et-bo-muted et-bo-strip__periods">Current: {s.periods}</span>}
					<span className="et-bo-pips" title={`${s.pips.length} current`}>
						{s.pips.map((p, i) => (
							<i key={i} style={{ background: (STATUS_STYLES[p] || STATUS_STYLES.not_started).fg }} />
						))}
					</span>
					{s.overdue > 0 && <span className="et-bo-late">{s.overdue} overdue</span>}
					{s.nextDue && (
						<span className={"et-bo-due" + (s.overdue > 0 ? " et-bo-due--late" : "")}>
							<Icon.Calendar size={11} />
							{fmt(s.nextDue)}
						</span>
					)}
					{href && (
						<Link to={href} className="et-bo-open" onClick={e => e.stopPropagation()} title="Open the client's Backoffice tab">
							<Icon.ExternalLink size={12} />
						</Link>
					)}
				</div>
			</div>
		);
	}
}

export class BackofficeBand extends PureComponent {
	render() {
		const { group } = this.props;
		const summary = group.backoffice || {};
		const rows = (summary.current || []).map(e => Object.assign({ _kind: "current" }, e)).concat((summary.lastCompleted || []).map(e => Object.assign({ _kind: "done" }, e)));
		const href = clientHref(group);
		return (
			<div className="et-subgrids et-bo-band ft-et-bo-band">
				<div className="et-bo-band__title">
					<Icon.Briefcase size={12} />
					<span>Backoffice engagements · current and last completed</span>
					<span className="et-bo-band__right">Read-only · from the Backoffice page</span>
				</div>
				<div className="et-bo-table">
					<div className="et-bo-table__head">
						<div>Engagement</div>
						<div>Period</div>
						<div>Tasks</div>
						<div>Hours</div>
						<div>Status</div>
						<div>Due</div>
					</div>
					{rows.map(e => {
						const budget = (e.budget && e.budget.total) || 0;
						const logged = (e.logged && e.logged.total) || 0;
						const pct = budget ? Math.min(100, Math.round((logged / budget) * 100)) : 0;
						return (
							<div key={e.id} className={"et-bo-table__row ft-et-bo-band-row" + (e._kind === "done" ? " et-bo-table__row--done" : "")}>
								<div>
									<span className="et-bo-bar" style={{ background: SERVICE_COLORS[e.service] }} />
									<span className="et-bo-table__name">
										{serviceLabel(e.service)} · {e.periodLabel}
									</span>
								</div>
								<div className="et-bo-mono">
									{fmt(e.periodStart)} – {fmt(e.periodEnd)}
								</div>
								<div className="et-bo-mono">
									{e.completeCount} / {e.taskCount}
								</div>
								<div>
									<span className="et-bo-meter">
										<i style={{ width: pct + "%", background: logged > budget && budget ? "#d92d20" : SERVICE_COLORS[e.service] }} />
									</span>
									<span className="et-bo-mono">
										{hours(logged)} / {hours(budget)}
									</span>
								</div>
								<div>
									<StatusPill status={e.status} small />
								</div>
								<div className={"et-bo-mono" + (e.status === "overdue" ? " et-bo-late" : "")}>{fmt(e.dueDate)}</div>
							</div>
						);
					})}
				</div>
				{href && (
					<div className="et-bo-band__foot">
						Showing the current period per service plus the last completed one.{" "}
						<Link to={href} className="et-bo-link">
							All backoffice engagements for this client
						</Link>
					</div>
				)}
			</div>
		);
	}
}

export class BackofficeDivider extends PureComponent {
	render() {
		const { group } = this.props;
		const href = clientHref(group);
		return (
			<div className="et-bo-divider ft-et-bo-divider">
				<Icon.Briefcase size={11} />
				<span>Backoffice · current periods</span>
				<span className="et-bo-divider__right">
					read-only · from the Backoffice page
					{href && (
						<>
							{" · "}
							<Link to={href} className="et-bo-link">
								show all periods
							</Link>
						</>
					)}
				</span>
			</div>
		);
	}
}

export class BackofficeRow extends PureComponent {
	onToggle = e => {
		e.preventDefault();
		e.stopPropagation();
		this.props.onToggle(this.props.engagement.id);
	};

	renderCell(d) {
		const { engagement } = this.props;
		const basis = d.kind === "native" ? "var(--etw-n_" + d.col.key + ", " + d.col.width + "px)" : "var(--etw-f_" + d.field.id + ", 220px)";
		const cell = backofficeCell(d, engagement);
		let body;
		if (cell.kind === "text") {
			body = (
				<span className="et-bo-cell-text" title={cell.value}>
					{cell.value}
					{cell.sub ? <small>{cell.sub}</small> : null}
				</span>
			);
		} else if (cell.kind === "service") {
			body = (
				<span className="et-bo-service">
					<span className="et-bo-tag et-bo-tag--sq">BO</span>
					<i style={{ background: SERVICE_COLORS[cell.value] }} />
					{SERVICE_SHORT[cell.value] || cell.value}
				</span>
			);
		} else if (cell.kind === "status") {
			body = <StatusPill status={cell.value} />;
		} else if (cell.kind === "date") {
			body = <span className={"et-bo-mono" + (cell.late ? " et-bo-late" : "")}>{cell.value || "—"}</span>;
		} else {
			body = <span className="et-bo-na">n/a</span>;
		}
		return (
			<div className={"et-grid__td et-bo-row__td" + (d.kind === "native" ? " et-grid__td--native" : "")} key={d.colKey} style={{ flex: "0 0 " + basis, width: basis }}>
				{body}
			</div>
		);
	}

	render() {
		const { engagement, orderedColumns, expanded } = this.props;
		return (
			<div className={"et-grid__row et-grid__row--grouped et-bo-row ft-et-bo-row" + (expanded ? " et-grid__row--expanded" : "")} data-bo-engagement={engagement.id}>
				<div className="et-grid__td et-grid__td--anchor et-grid__td--anchor-exp">
					<button className={"et-row-exp ft-et-bo-row-toggle " + (expanded ? "et-row-exp--open" : "")} onClick={this.onToggle} title={expanded ? "Hide tasks" : "Show tasks"}>
						<Icon.ChevronRight size={13} />
					</button>
					<span className="et-grid__client-link">
						<span className="et-grid__client-name">{engagement.clientName}</span>
						<span className="et-grid__engagement-name">{engagement.name}</span>
					</span>
				</div>
				{(orderedColumns || []).map(d => this.renderCell(d))}
			</div>
		);
	}
}

export class BackofficeTasks extends PureComponent {
	render() {
		const { engagement } = this.props;
		const today = moment().format("YYYY-MM-DD");
		const tasks = engagement.tasks || [];
		return (
			<div className="et-subgrids et-bo-band ft-et-bo-tasks">
				<div className="et-bo-band__title">
					<Icon.List size={12} />
					<span>
						Tasks · {engagement.completeCount} / {engagement.taskCount} complete
					</span>
					<span className="et-bo-band__right">Read-only</span>
				</div>
				<div className="et-bo-table et-bo-table--tasks">
					<div className="et-bo-table__head">
						<div>Task</div>
						<div>Start → Due</div>
						<div>Hours</div>
						<div>Status</div>
					</div>
					{tasks.map(t => {
						const late = t.status !== "complete" && t.dueDate && t.dueDate < today;
						const b = t.budget || {};
						const l = t.logged || {};
						const budget = (b.preparer || 0) + (b.reviewer || 0) + (b.manager || 0);
						const logged = (l.preparer || 0) + (l.reviewer || 0) + (l.manager || 0);
						return (
							<div key={t.id} className={"et-bo-table__row" + (t.status === "complete" ? " et-bo-table__row--done" : "")}>
								<div>
									<span className="et-bo-table__name" title={t.title}>
										{t.groupName ? <span className="et-bo-muted">{t.groupName} · </span> : null}
										{t.title}
									</span>
								</div>
								<div className={"et-bo-mono" + (late ? " et-bo-late" : "")}>
									{fmt(t.startDate)} → {fmt(t.dueDate)}
								</div>
								<div className="et-bo-mono">
									{hours(logged)} / {hours(budget)}
								</div>
								<div>
									<StatusPill status={late ? "overdue" : t.status} small />
								</div>
							</div>
						);
					})}
					{tasks.length === 0 && <div className="et-bo-table__empty">No tasks in this period.</div>}
				</div>
			</div>
		);
	}
}
